import { environment } from '../../environments/environment';
import { RequestSnippet, RequestSnippetBuilder, RequestSnippetHeaders } from './request-snippet';

function addIndentation(str: string): string {
    return `${environment.snippetIndentation}${str}`;
}

function formatJson(data: any): string {
    return JSON.stringify(data, null, environment.snippetIndentation);
}

export const buildCurlSnippet: RequestSnippetBuilder = (snippet: RequestSnippet): string => {
    const codeLines: Array<string> = [
        `curl -X ${snippet.method}`,
        `'${snippet.url}'`
    ];

    const headers: RequestSnippetHeaders = snippet.headers ? {
        ...snippet.headers
    } : {};
    headers['Content-Type'] = 'application/json';

    const headerLines: Array<string> = Object.entries(headers).map(([header, value]): string => {
        return `-H '${header}: ${value}'`;
    });
    codeLines.push(...headerLines);

    if (snippet.data) {
        let json: string = formatJson(snippet.data);
        json = json.replace(/\n/g, `\n${environment.snippetIndentation}`);

        codeLines.push(`-d '${json}'`);
    }

    const comment: string = snippet.comment ? `# ${snippet.comment}\n\n` : '';
    const code: string = codeLines.map((line: string, index: number): string => {
        return index ? addIndentation(`${line}`) : line;
    }).join(' \\\n');

    return `${comment}${code}`;
};

export const buildPythonSnippet: RequestSnippetBuilder = (snippet: RequestSnippet): string => {
    const codeLines: Array<string> = [
        'import requests',
        '',
        `url = '${snippet.url}'`
    ];
    const requestArgs: Array<string> = [
        `url=url`
    ];

    if (snippet.headers) {
        const headerLines: Array<string> = Object.entries(snippet.headers).map(([header, value]): string => {
            return `'${header}': '${value}',`;
        }).map(addIndentation);

        codeLines.push(
            '',
            'headers = {',
            ...headerLines,
            '}'
        );

        requestArgs.push('headers=headers');
    }

    if (snippet.data) {
        codeLines.push(
            '',
            `data = ${formatJson(snippet.data)}`
        );

        requestArgs.push('json=data');
    }

    codeLines.push(
        '',
        `response = requests.${snippet.method.toLowerCase()}(${requestArgs.join(', ')})`
    );

    const comment: string = snippet.comment ? `# ${snippet.comment}\n\n` : '';
    const code: string = codeLines.join('\n');

    return `${comment}${code}`;
};

export const buildNodejsSnippet: RequestSnippetBuilder = (snippet: RequestSnippet): string => {
    const codeLines: Array<string> = [
        `var request = require('request');`,
        ''
    ];
    const requestOptions: Array<string> = [
        `url: '${snippet.url}'`,
        `method: '${snippet.method}'`
    ];

    if (snippet.headers) {
        const headerLines: Array<string> = Object.entries(snippet.headers).map(([header, value]): string => {
            return `'${header}': '${value}'`;
        }).map(addIndentation);

        codeLines.push(
            'var headers = {',
            headerLines.join(',\n'),
            '};'
        );

        requestOptions.push('headers: headers');
    }

    if (snippet.data) {
        codeLines.push(
            `var data = ${formatJson(snippet.data)};`
        );

        requestOptions.push('json: data');
    } else {
        requestOptions.push('json: true');
    }

    codeLines.push(
        '',
        'var options = {',
        requestOptions.map(addIndentation).join(',\n'),
        '};',
        '',
        'function callback(error, response, body) {',
        addIndentation('if (!error && response.statusCode >= 200 && response.statusCode < 300) {'),
        addIndentation(addIndentation('console.log(body);')),
        addIndentation('}'),
        '}',
        '',
        'request(options, callback);'
    );

    const comment: string = snippet.comment ? `// ${snippet.comment}\n\n` : '';
    const code: string = codeLines.join('\n');

    return `${comment}${code}`;
};
